import { v7 as uuidv7 } from "uuid";

import { ApiError, NetworkError, PreconditionFailedError, UnauthorizedError } from "./errors";
import { parseProblemResponse } from "./problem";
import { getCurrentBranchId } from "@/lib/branch/branch-store";

export const API_BASE = "/api/v1";
export const ACTING_BRANCH_HEADER = "X-Acting-Branch-Id";

/** Server-side page-size cap; buildQuery clamps `limit` to this. */
export const MAX_PAGE_LIMIT = 200;

/** Cursor-paginated list envelope (`next_cursor: null` means last page). */
export interface Page<T> {
  items: T[];
  next_cursor: string | null;
}

export type QueryValue = string | number | boolean | null | undefined;

export interface QueryParams {
  cursor?: string | null;
  limit?: number;
  [key: string]: QueryValue;
}

export type HttpMethod = "GET" | "POST" | "PUT" | "PATCH" | "DELETE";

/**
 * Already-materialized request bytes whose media type must remain byte-for-byte
 * stable across every transport attempt. This is required for idempotent
 * multipart uploads because native FormData chooses a fresh boundary whenever
 * fetch serializes it.
 */
export interface SerializedRequestBody {
  bytes: ArrayBuffer;
  contentType: string;
}

export interface ApiRequestOptions {
  method?: HttpMethod;
  /** JSON-serialized when defined, except non-retryable FormData. */
  body?: unknown;
  /** Exact body bytes and Content-Type, mutually exclusive with `body`. */
  serializedBody?: SerializedRequestBody;
  /**
   * Successful non-204/205 responses require a JSON body by default. Set to
   * `none` only for an operation whose contract explicitly declares a
   * bodyless success status.
   */
  responseBody?: "json" | "none";
  /** Appended as a query string via buildQuery (limit clamped to 200). */
  query?: QueryParams;
  headers?: Record<string, string>;
  /**
   * Idempotency key for POST. When omitted, one UUIDv7 is generated per
   * logical call and reused verbatim across internal retries, so the server
   * can deduplicate replays.
   */
  idempotencyKey?: string;
  /** OpenAPI x-idempotency policy; POST defaults to client-key. */
  idempotency?: "client-key" | "derived" | "none";
  /** Sets If-Match for optimistic-concurrency writes (pair with a previously captured ETag). */
  ifMatch?: string;
  signal?: AbortSignal;
  /**
   * Suppress the global "erp:unauthorized" window event for this call.
   * For endpoints where a 401 is an expected outcome rather than an expired
   * session (session probe, login attempt). UnauthorizedError is still thrown.
   */
  suppressUnauthorizedEvent?: boolean;
  /**
   * Operational calls carry the branch selected by the authenticated shell.
   * Company-wide calls (the default, including session and Finance) never do.
   */
  branchScope?: "company" | "operational";
}

export interface ApiResponse<T> {
  data: T;
  /** Raw ETag response header, for If-Match on a later write. Null when absent. */
  etag: string | null;
  status: number;
  /** Read-only response headers for protocol-level workflows such as polling. */
  headers: Headers;
}

/**
 * Build a query string from params. `undefined`/`null` values are omitted;
 * `limit` is clamped to 1..MAX_PAGE_LIMIT. Returns "" or a string starting
 * with "?".
 */
export function buildQuery(params?: QueryParams): string {
  if (!params) return "";
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null) continue;
    if (key === "limit" && typeof value === "number") {
      const clamped = Math.min(Math.max(1, Math.trunc(value)), MAX_PAGE_LIMIT);
      search.set(key, String(clamped));
      continue;
    }
    search.set(key, String(value));
  }
  const qs = search.toString();
  return qs === "" ? "" : `?${qs}`;
}

/**
 * Resolve against the document origin when one exists (browser, jsdom tests);
 * plain relative path otherwise. SSR-safe: no bare `location` access.
 */
function resolveUrl(pathWithQuery: string): string {
  if (typeof location !== "undefined") {
    return new URL(pathWithQuery, location.origin).toString();
  }
  return pathWithQuery;
}

function requestIdOf(res: Response): string | undefined {
  return res.headers.get("x-request-id") ?? undefined;
}

async function readSuccessfulJsonBody<T>(res: Response): Promise<T> {
  let text: string;
  try {
    text = await res.text();
  } catch (cause) {
    // The handler may already have committed even though the response stream
    // was reset or truncated. Treat this exactly like an ambiguous network
    // failure so an idempotent retry keeps the same key.
    throw new NetworkError("Successful response body could not be read", { cause });
  }
  if (text === "") {
    // A body-bearing handler may already have committed. Empty 201/202 bytes
    // are indistinguishable from a cleanly truncated response and therefore
    // retain/retry the same idempotency key.
    throw new NetworkError("Successful response body was empty");
  }
  try {
    return JSON.parse(text) as T;
  } catch (cause) {
    // A 2xx status followed by malformed JSON is also commit-ambiguous. It
    // must never be classified as a definitive ApiError that rotates a key.
    throw new NetworkError("Successful response body was not valid JSON", { cause });
  }
}

async function handleResponse<T>(res: Response, opts: ApiRequestOptions): Promise<ApiResponse<T>> {
  const etag = res.headers.get("etag");
  const requestId = requestIdOf(res);

  if (res.ok) {
    if (res.status === 204 || res.status === 205 || opts.responseBody === "none") {
      return { data: undefined as T, etag, status: res.status, headers: res.headers };
    }
    const data = await readSuccessfulJsonBody<T>(res);
    return { data, etag, status: res.status, headers: res.headers };
  }

  const problem = (await parseProblemResponse(res)) ?? undefined;

  if (res.status === 401) {
    if (!opts.suppressUnauthorizedEvent && typeof window !== "undefined") {
      window.dispatchEvent(new CustomEvent("erp:unauthorized"));
    }
    throw new UnauthorizedError(problem, requestId);
  }
  if (res.status === 412) {
    throw new PreconditionFailedError(problem, requestId);
  }
  throw new ApiError(
    problem?.title ?? `Request failed with status ${res.status}`,
    res.status,
    problem,
    requestId,
  );
}

/**
 * Low-level request. Returns data plus status and response headers so callers
 * can implement protocol workflows (ETag, Location, Retry-After); most code
 * uses apiFetch.
 *
 * Retry policy: at most one retry on NetworkError and one on a 5xx response,
 * and only for idempotent-safe requests — GET always; POST only when its
 * explicit/default x-idempotency policy is client-key or derived. `none`
 * operations neither carry a key nor retry an ambiguous failure.
 * PUT/PATCH/DELETE are never retried automatically: their If-Match/version
 * semantics make the caller decide.
 */
export async function apiRequest<T>(
  path: string,
  opts: ApiRequestOptions = {},
): Promise<ApiResponse<T>> {
  const method = opts.method ?? "GET";
  const idempotency = opts.idempotency ?? (method === "POST" ? "client-key" : "none");
  const url = resolveUrl(API_BASE + path + buildQuery(opts.query));

  const headers = new Headers(opts.headers);
  if (opts.body !== undefined && opts.serializedBody !== undefined) {
    throw new TypeError("apiRequest body and serializedBody are mutually exclusive");
  }
  // This authority header is transport-owned. Delete every caller spelling
  // first so a mixed-case duplicate cannot become a comma-joined value.
  headers.delete(ACTING_BRANCH_HEADER);
  if (opts.branchScope === "operational") {
    const actingBranchId = getCurrentBranchId();
    if (actingBranchId !== null) headers.set(ACTING_BRANCH_HEADER, actingBranchId);
  }
  if (!headers.has("Accept")) {
    headers.set("Accept", "application/json, application/problem+json");
  }

  let body: BodyInit | undefined;
  if (opts.serializedBody !== undefined) {
    if (headers.has("Content-Type")) {
      throw new TypeError("serializedBody owns the Content-Type header");
    }
    if (opts.serializedBody.contentType.trim() === "") {
      throw new TypeError("serializedBody requires a non-empty Content-Type");
    }
    headers.set("Content-Type", opts.serializedBody.contentType);
    // Own a snapshot so caller mutation cannot alter a later retry attempt.
    body = opts.serializedBody.bytes.slice(0);
  } else if (opts.body !== undefined) {
    if (typeof FormData !== "undefined" && opts.body instanceof FormData) {
      if (method === "POST" && idempotency !== "none") {
        throw new TypeError(
          "Retryable FormData must be materialized as serializedBody so its multipart boundary is stable",
        );
      }
      // Non-retryable uploads may let fetch choose their multipart boundary.
      body = opts.body;
    } else {
      if (!headers.has("Content-Type")) {
        headers.set("Content-Type", "application/json");
      }
      body = JSON.stringify(opts.body);
    }
  }

  if (idempotency !== "client-key") {
    headers.delete("Idempotency-Key");
  } else if (method === "POST" && !headers.has("Idempotency-Key")) {
    // Generated exactly once per logical call; the retry loop below reuses it.
    headers.set("Idempotency-Key", opts.idempotencyKey ?? uuidv7());
  }

  if (opts.ifMatch !== undefined) {
    headers.set("If-Match", opts.ifMatch);
  }

  const retryable = method === "GET" || (method === "POST" && idempotency !== "none");
  let networkRetriesLeft = retryable ? 1 : 0;
  let serverErrorRetriesLeft = retryable ? 1 : 0;

  for (;;) {
    let res: Response;
    try {
      // A caller can supply a signal from another JS realm (notably an
      // embedded app or jsdom). Native fetch rejects that object before a
      // request exists; same-realm browser signals—the production path—are
      // forwarded unchanged and retain cancellation semantics.
      let transportSignal = opts.signal;
      if (transportSignal !== undefined) {
        try {
          void new Request("data:,signal-check", { signal: transportSignal });
        } catch {
          transportSignal = undefined;
        }
      }
      res = await fetch(url, {
        method,
        headers,
        body,
        credentials: "include",
        signal: transportSignal,
      });
    } catch (cause) {
      if (opts.signal?.aborted) throw cause;
      if (networkRetriesLeft > 0) {
        networkRetriesLeft -= 1;
        continue;
      }
      throw new NetworkError(undefined, { cause });
    }

    if (res.status >= 500 && serverErrorRetriesLeft > 0) {
      serverErrorRetriesLeft -= 1;
      continue;
    }

    try {
      return await handleResponse<T>(res, opts);
    } catch (error) {
      if (opts.signal?.aborted) throw opts.signal.reason ?? error;
      if (error instanceof NetworkError && networkRetriesLeft > 0) {
        networkRetriesLeft -= 1;
        continue;
      }
      throw error;
    }
  }
}

/** Standard JSON call — resolves to the response data only. */
export async function apiFetch<T>(path: string, opts: ApiRequestOptions = {}): Promise<T> {
  const { data } = await apiRequest<T>(path, opts);
  return data;
}

/** Operational request using the shell's current acting branch, when set. */
export function apiRequestOperational<T>(
  path: string,
  opts: ApiRequestOptions = {},
): Promise<ApiResponse<T>> {
  return apiRequest<T>(path, { ...opts, branchScope: "operational" });
}

/** Data-only form of apiRequestOperational. */
export async function apiFetchOperational<T>(
  path: string,
  opts: ApiRequestOptions = {},
): Promise<T> {
  const { data } = await apiRequestOperational<T>(path, opts);
  return data;
}
